import { CircleAlert, Hourglass, LogOut, SearchX, WifiOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { formatMeetingId, type MeetingPublicInfo } from '../../shared/protocol';
import { Brand } from '../components/Brand';
import { Button } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { takeJoinPrefs } from '../lib/joinPrefs';
import { navigate } from '../lib/router';
import { settingsStore } from '../lib/settings';
import { getOwnedMeeting } from '../lib/storage';
import { useStore } from '../lib/store';
import { MeetingSession, type MeetingState } from '../meeting/MeetingSession';
import { MeetingRoom } from './MeetingRoom';
import { PreJoin, type PreJoinResult } from './PreJoin';
import '../styles/meeting.css';

type Stage = { kind: 'loading' } | { kind: 'error'; message: string; notFound: boolean } | { kind: 'prejoin'; meeting: MeetingPublicInfo } | { kind: 'session' };

export function MeetingPage({ id, passcode: urlPasscode }: { id: string; passcode: string | null }) {
  const owned = getOwnedMeeting(id);
  const prefs = useRef(takeJoinPrefs());
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [passcodeError, setPasscodeError] = useState<string | null>(null);
  const [manualPasscode, setManualPasscode] = useState<string | null>(null);
  const [session, setSession] = useState<MeetingSession | null>(null);
  const meetingRef = useRef<MeetingPublicInfo | null>(null);

  const load = () => {
    setStage({ kind: 'loading' });
    api
      .getMeeting(id)
      .then((meeting) => {
        meetingRef.current = meeting;
        setStage({ kind: 'prejoin', meeting });
      })
      .catch((err: ApiError) => setStage({ kind: 'error', message: err.message, notFound: err.status === 404 }));
  };

  useEffect(() => {
    document.title = `Reunión ${formatMeetingId(id)} · Nexo Meet`;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => () => session?.dispose(), [session]);

  const passcode = manualPasscode ?? urlPasscode ?? owned?.passcode ?? null;
  const needsPasscode = stage.kind === 'prejoin' && stage.meeting.hasPasscode && !owned && (!passcode || !!passcodeError);

  const join = (result: PreJoinResult) => {
    const s = new MeetingSession();
    const code = result.passcode ?? passcode;
    if (result.passcode) setManualPasscode(result.passcode);
    setPasscodeError(null);
    setSession(s);
    setStage({ kind: 'session' });
    void s.join({
      meetingId: id,
      name: result.name,
      passcode: code,
      hostKey: owned?.hostKey ?? null,
      micTrack: result.micTrack,
      cameraTrack: result.cameraTrack,
      audioMuted: result.audioMuted,
      videoOn: result.videoOn,
    });
  };

  // A rejected passcode sends the user back to the pre-join screen with the field visible.
  const failure = useStore(session?.store ?? EMPTY_STORE, (s) => s.failure);
  useEffect(() => {
    if (failure?.code === 'bad-passcode' && meetingRef.current) {
      session?.dispose();
      setSession(null);
      setManualPasscode(null);
      setPasscodeError(failure.message);
      setStage({ kind: 'prejoin', meeting: meetingRef.current });
    }
  }, [failure, session]);

  if (stage.kind === 'loading') {
    return (
      <CenterCard>
        <div className="spinner" aria-label="Cargando" />
        <p className="muted">Buscando la reunión…</p>
      </CenterCard>
    );
  }
  if (stage.kind === 'error') {
    return (
      <CenterCard>
        {stage.notFound ? <SearchX size={40} /> : <WifiOff size={40} />}
        <h1>{stage.notFound ? 'Reunión no encontrada' : 'No se pudo cargar la reunión'}</h1>
        <p className="muted">{stage.notFound ? `No existe ninguna reunión con el ID ${formatMeetingId(id)}. Puede que se haya eliminado o que el enlace esté incompleto.` : stage.message}</p>
        <div className="row">
          <Button onClick={() => navigate('/')}>Volver al inicio</Button>
          {!stage.notFound && (
            <Button variant="primary" onClick={load}>
              Reintentar
            </Button>
          )}
        </div>
      </CenterCard>
    );
  }
  if (stage.kind === 'prejoin') {
    const s = settingsStore.get();
    const name = prefs.current?.name ?? s.displayName;
    return (
      <PreJoin
        meeting={stage.meeting}
        isHost={!!owned}
        initialName={name}
        initialAudioMuted={prefs.current?.audioMuted ?? s.joinMuted}
        initialVideoOff={prefs.current?.videoOff ?? s.joinVideoOff}
        needsPasscode={needsPasscode}
        passcodeError={passcodeError}
        autoJoin={!s.showPreview && !!name && !needsPasscode && !passcodeError}
        onJoin={join}
        onCancel={() => navigate('/')}
      />
    );
  }
  return session ? <SessionView session={session} onRejoin={load} /> : null;
}

const EMPTY_STORE = { get: () => ({}) as MeetingState, set() {}, patch() {}, subscribe: () => () => undefined };

function SessionView({ session, onRejoin }: { session: MeetingSession; onRejoin: () => void }) {
  const phase = useStore(session.store, (s) => s.phase);
  const failure = useStore(session.store, (s) => s.failure);
  const ended = useStore(session.store, (s) => s.ended);
  const meeting = useStore(session.store, (s) => s.meeting);

  if (phase === 'connecting' || phase === 'idle') {
    return (
      <CenterCard dark>
        <div className="spinner" aria-label="Conectando" />
        <h1>Conectando…</h1>
        <p className="muted">{failure?.message ?? 'Estableciendo una conexión segura con la reunión.'}</p>
        <Button variant="ghost" onClick={() => session.leave()}>
          Cancelar
        </Button>
      </CenterCard>
    );
  }
  if (phase === 'failed') {
    return (
      <CenterCard>
        <CircleAlert size={40} />
        <h1>No puedes entrar en esta reunión</h1>
        <p className="muted">{failure?.message}</p>
        <div className="row">
          <Button onClick={() => navigate('/')}>Volver al inicio</Button>
          {(failure?.code === 'network' || failure?.code === 'locked' || failure?.code === 'full') && (
            <Button variant="primary" onClick={onRejoin}>
              Intentar de nuevo
            </Button>
          )}
        </div>
      </CenterCard>
    );
  }
  if (phase === 'waiting') {
    return (
      <CenterCard dark>
        <Hourglass size={40} className="pulse" />
        <h1>Espera, el anfitrión te dejará entrar pronto</h1>
        <p className="muted">{meeting?.title}</p>
        <Button variant="ghost" onClick={() => session.leave()}>
          <LogOut size={16} /> Salir
        </Button>
      </CenterCard>
    );
  }
  if (phase === 'ended') {
    const reason = ended?.reason;
    const title =
      reason === 'host-ended'
        ? 'El anfitrión ha finalizado la reunión'
        : reason === 'removed'
          ? 'Has sido retirado de la reunión'
          : reason === 'denied'
            ? 'El anfitrión no te ha admitido'
            : reason === 'expired'
              ? 'La reunión se ha eliminado'
              : 'Has salido de la reunión';
    return (
      <CenterCard>
        <LogOut size={40} />
        <h1>{title}</h1>
        {ended?.byName && reason !== 'left' && <p className="muted">Acción realizada por {ended.byName}.</p>}
        <div className="row">
          <Button onClick={() => navigate('/')}>Volver al inicio</Button>
          {reason === 'left' && (
            <Button variant="primary" onClick={onRejoin}>
              Volver a entrar
            </Button>
          )}
        </div>
      </CenterCard>
    );
  }
  return <MeetingRoom session={session} />;
}

function CenterCard({ children, dark }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <div className={`center-screen ${dark ? 'meeting-theme' : ''}`}>
      <div className="center-brand">
        <Brand />
      </div>
      <div className="center-card" role="status">
        {children}
      </div>
    </div>
  );
}
